import { SubmissionStepType } from "generated-graphql/graphql";
import { useWasteStepper } from "hooks/useWasteStepper";
import { useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { WASTE_SUBMISSION_STEP_PRESENTATION } from "util/wasteSubmissionStepMetadata";

/**
 * Builds the path to a waste submission stepper step. The overview lives at the
 * empty slug; step routes append their `routeSlug` (e.g. `.../stepper/contacts`).
 */
export function stepperStepPath(tenantId: string, submissionId: string, routeSlug: string) {
  return `/o/${tenantId}/waste/submissions/${submissionId}/stepper/${routeSlug}`;
}

/**
 * Navigation for the waste submission stepper. Unlike TierII's
 * `useNavigateToNextStep`, this does NOT skip completed steps — it simply moves
 * to the next step in the backend-ordered `stepperSteps` list.
 *
 * If the current step is the last one (or not found), navigation falls back to
 * the stepper overview (empty route slug).
 */
export function useStepperNavigation() {
  const { tenantId = "", submissionId = "" } = useParams<{
    tenantId: string;
    submissionId: string;
  }>();
  const navigate = useNavigate();
  const { submission } = useWasteStepper();

  const routeForSlug = useCallback(
    (routeSlug: string) => stepperStepPath(tenantId, submissionId, routeSlug),
    [tenantId, submissionId]
  );

  const goToStep = useCallback(
    (step: SubmissionStepType) => {
      navigate(routeForSlug(WASTE_SUBMISSION_STEP_PRESENTATION[step].routeSlug));
    },
    [navigate, routeForSlug]
  );

  /**
   * Navigates to the step immediately following `currentStep` in the ordered
   * `stepperSteps`. Falls back to the overview when the current step is last or
   * missing.
   */
  const goToNextStep = useCallback(
    (currentStep: SubmissionStepType) => {
      const orderedSteps = (submission?.stepperSteps ?? []).map(({ step }) => step);
      const currentIndex = orderedSteps.findIndex((step) => step === currentStep);
      const nextStep = currentIndex >= 0 ? orderedSteps[currentIndex + 1] : undefined;

      if (nextStep) {
        goToStep(nextStep);
        return;
      }

      // Last (or unknown) step: return to the overview.
      navigate(routeForSlug(""));
    },
    [submission, goToStep, navigate, routeForSlug]
  );

  /**
   * Navigates to the step immediately preceding `currentStep` in the ordered
   * `stepperSteps`. A no-op when the current step is first or missing —
   * deliberately NOT the overview fallback `goToNextStep` uses: document-form
   * flows have no overview, and their index route would just bounce back to
   * the first step.
   */
  const goToPreviousStep = useCallback(
    (currentStep: SubmissionStepType) => {
      const orderedSteps = (submission?.stepperSteps ?? []).map(({ step }) => step);
      const currentIndex = orderedSteps.findIndex((step) => step === currentStep);
      const previousStep = currentIndex > 0 ? orderedSteps[currentIndex - 1] : undefined;

      if (previousStep) {
        goToStep(previousStep);
      }
    },
    [submission, goToStep]
  );

  /** Return to the stepper overview (the empty route slug). */
  const goToOverview = useCallback(() => navigate(routeForSlug("")), [navigate, routeForSlug]);

  return { goToStep, goToNextStep, goToPreviousStep, goToOverview };
}
